/**
 * Master Operator Portal
 * Superadmin interface for managing all EdgeLex tenants
 *
 * Features:
 * - Login using Keycloak master realm credentials
 * - View all tenants (Keycloak realms + EdgeLex data)
 * - Disable/Enable tenants
 * - Delete tenants (with confirmation)
 */
import { useState, useEffect } from 'react';

interface TenantInfo {
  realmId: string;
  realmName: string;
  displayName?: string;
  firmName?: string;
  status: 'active' | 'disabled' | 'orphaned';
  sources: {
    keycloak: boolean;
    firmSettings: boolean;
    edgeMail: boolean;
  };
  stats?: {
    userCount: number;
    mailboxCount: number;
    domainCount: number;
  };
  createdAt?: string;
  firmId?: string;
  edgeMailTenantId?: string;
}

interface DeleteResult {
  success: boolean;
  realmId: string;
  deletedFrom: {
    keycloak: boolean;
    firmSettings: boolean;
    users: number;
    edgeMailTenant: boolean;
    edgeMailMailboxes: number;
    edgeMailDomains: number;
  };
  errors: string[];
}

export default function MasterOperatorPortal() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [masterToken, setMasterToken] = useState<string | null>(null);
  const [tenants, setTenants] = useState<TenantInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionResult, setActionResult] = useState<{ type: string; result: any } | null>(null);

  // Login form state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState<string | null>(null);
  const [loginLoading, setLoginLoading] = useState(false);

  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState<{ realmId: string; confirmText: string } | null>(null);

  // Check for existing token on mount
  useEffect(() => {
    const storedToken = sessionStorage.getItem('masterOperatorToken');
    if (storedToken) {
      setMasterToken(storedToken);
      setIsAuthenticated(true);
    }
  }, []);

  // Fetch tenants when authenticated
  useEffect(() => {
    if (isAuthenticated && masterToken) {
      fetchTenants();
    }
  }, [isAuthenticated, masterToken]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError(null);

    try {
      // Use backend proxy to avoid CORS issues
      const response = await fetch('/api/master/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Invalid credentials');
      }

      const token = data.access_token;

      sessionStorage.setItem('masterOperatorToken', token);
      setMasterToken(token);
      setIsAuthenticated(true);
      setUsername('');
      setPassword('');
    } catch (err) {
      setLoginError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('masterOperatorToken');
    setMasterToken(null);
    setIsAuthenticated(false);
    setTenants([]);
  };

  const fetchTenants = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/master/tenants', {
        headers: { 'Authorization': `Bearer ${masterToken}` }
      });

      if (!response.ok) {
        if (response.status === 401) {
          handleLogout();
          throw new Error('Session expired. Please login again.');
        }
        throw new Error('Failed to fetch tenants');
      }

      const data = await response.json();
      setTenants(data.tenants || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async (realmId: string) => {
    if (!confirm(`Disable tenant "${realmId}"? Users will not be able to login.`)) return;

    try {
      const response = await fetch(`/api/master/tenants/${realmId}/disable`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${masterToken}` }
      });

      const result = await response.json();
      setActionResult({ type: 'disable', result });
      fetchTenants();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disable tenant');
    }
  };

  const handleEnable = async (realmId: string) => {
    try {
      const response = await fetch(`/api/master/tenants/${realmId}/enable`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${masterToken}` }
      });

      const result = await response.json();
      setActionResult({ type: 'enable', result });
      fetchTenants();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to enable tenant');
    }
  };

  const handleDelete = async (realmId: string) => {
    if (deleteConfirm?.confirmText !== realmId) {
      setError('Confirmation text does not match realm ID');
      return;
    }

    try {
      const response = await fetch(`/api/master/tenants/${realmId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${masterToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ confirm: realmId })
      });

      const result: DeleteResult = await response.json();
      setActionResult({ type: 'delete', result });
      setDeleteConfirm(null);
      fetchTenants();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete tenant');
    }
  };

  // Login Form
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-gray-800 rounded-xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">Master Operator Portal</h1>
            <p className="text-gray-400">EdgeLex Superadmin Access</p>
          </div>

          {loginError && (
            <div className="bg-red-900/50 border border-red-500 text-red-300 px-4 py-3 rounded-lg mb-6">
              {loginError}
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Keycloak Admin Username
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-lex-accent focus:border-transparent"
                placeholder="admin"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-lex-accent focus:border-transparent"
                required
              />
            </div>

            <button
              type="submit"
              disabled={loginLoading}
              className="w-full py-3 px-4 bg-lex-accent hover:bg-lex-accent-hover disabled:bg-lex-accent-hover text-lex-accent-contrast font-medium rounded-lg transition-colors"
            >
              {loginLoading ? 'Authenticating...' : 'Login to Master Portal'}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-gray-500">
            Use Keycloak master realm credentials
          </p>
        </div>
      </div>
    );
  }

  // Main Dashboard
  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div>
            <h1 className="text-2xl font-bold">Master Operator Portal</h1>
            <p className="text-gray-400 text-sm">EdgeLex Tenant Management</p>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={fetchTenants}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm transition-colors"
            >
              Refresh
            </button>
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-sm transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Error Display */}
        {error && (
          <div className="bg-red-900/50 border border-red-500 text-red-300 px-4 py-3 rounded-lg mb-6">
            {error}
            <button onClick={() => setError(null)} className="float-right text-red-400 hover:text-red-300">
              &times;
            </button>
          </div>
        )}

        {/* Action Result */}
        {actionResult && (
          <div className="bg-lex-accent-soft border border-lex-accent text-lex-accent px-4 py-3 rounded-lg mb-6">
            <h4 className="font-bold mb-2">
              {actionResult.type.charAt(0).toUpperCase() + actionResult.type.slice(1)} Result:
            </h4>
            <pre className="text-xs overflow-auto">{JSON.stringify(actionResult.result, null, 2)}</pre>
            <button onClick={() => setActionResult(null)} className="mt-2 text-lex-accent hover:text-lex-accent-hover">
              Dismiss
            </button>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-gray-800 rounded-xl p-6 max-w-md w-full mx-4">
              <h3 className="text-xl font-bold text-red-400 mb-4">Confirm Deletion</h3>
              <p className="text-gray-300 mb-4">
                This will permanently delete tenant <span className="font-mono text-yellow-400">{deleteConfirm.realmId}</span> and ALL associated data:
              </p>
              <ul className="text-gray-400 text-sm mb-4 list-disc list-inside">
                <li>Keycloak realm and all users</li>
                <li>Firm settings and configuration</li>
                <li>All user accounts in EdgeLex</li>
                <li>EdgeMail tenant, mailboxes, and domains</li>
              </ul>
              <p className="text-red-400 mb-4">
                Type <span className="font-mono bg-gray-700 px-2 py-1 rounded">{deleteConfirm.realmId}</span> to confirm:
              </p>
              <input
                type="text"
                value={deleteConfirm.confirmText}
                onChange={(e) => setDeleteConfirm({ ...deleteConfirm, confirmText: e.target.value })}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white mb-4"
                placeholder="Type realm ID to confirm"
              />
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 py-2 px-4 bg-gray-600 hover:bg-gray-500 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(deleteConfirm.realmId)}
                  disabled={deleteConfirm.confirmText !== deleteConfirm.realmId}
                  className="flex-1 py-2 px-4 bg-red-600 hover:bg-red-700 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg transition-colors"
                >
                  Delete Forever
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-gray-800 rounded-lg p-4">
            <h3 className="text-gray-400 text-sm">Total Tenants</h3>
            <p className="text-3xl font-bold">{tenants.length}</p>
          </div>
          <div className="bg-gray-800 rounded-lg p-4">
            <h3 className="text-gray-400 text-sm">Active</h3>
            <p className="text-3xl font-bold text-green-400">
              {tenants.filter(t => t.status === 'active').length}
            </p>
          </div>
          <div className="bg-gray-800 rounded-lg p-4">
            <h3 className="text-gray-400 text-sm">Disabled</h3>
            <p className="text-3xl font-bold text-yellow-400">
              {tenants.filter(t => t.status === 'disabled').length}
            </p>
          </div>
          <div className="bg-gray-800 rounded-lg p-4">
            <h3 className="text-gray-400 text-sm">Orphaned</h3>
            <p className="text-3xl font-bold text-red-400">
              {tenants.filter(t => t.status === 'orphaned').length}
            </p>
          </div>
        </div>

        {/* Tenant List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lex-accent mx-auto"></div>
            <p className="mt-4 text-gray-400">Loading tenants...</p>
          </div>
        ) : (
          <div className="bg-gray-800 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Realm / Firm</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Sources</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Stats</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-300">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {tenants.map((tenant) => (
                  <tr key={tenant.realmId} className="hover:bg-gray-750">
                    <td className="px-4 py-4">
                      <div className="font-medium text-white">{tenant.firmName || tenant.realmName}</div>
                      <div className="text-sm text-gray-400 font-mono">{tenant.realmId}</div>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        tenant.status === 'active' ? 'bg-green-900/50 text-green-400' :
                        tenant.status === 'disabled' ? 'bg-yellow-900/50 text-yellow-400' :
                        'bg-red-900/50 text-red-400'
                      }`}>
                        {tenant.status}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex gap-2">
                        <span className={`text-xs ${tenant.sources.keycloak ? 'text-green-400' : 'text-gray-600'}`}>
                          KC
                        </span>
                        <span className={`text-xs ${tenant.sources.firmSettings ? 'text-green-400' : 'text-gray-600'}`}>
                          DB
                        </span>
                        <span className={`text-xs ${tenant.sources.edgeMail ? 'text-green-400' : 'text-gray-600'}`}>
                          EM
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-400">
                      {tenant.stats && (
                        <span>
                          {tenant.stats.userCount} users, {tenant.stats.mailboxCount} mailboxes
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div className="flex gap-2 justify-end">
                        {tenant.status === 'active' ? (
                          <button
                            onClick={() => handleDisable(tenant.realmId)}
                            className="px-3 py-1 bg-yellow-600 hover:bg-yellow-700 text-sm rounded transition-colors"
                          >
                            Disable
                          </button>
                        ) : tenant.status === 'disabled' ? (
                          <button
                            onClick={() => handleEnable(tenant.realmId)}
                            className="px-3 py-1 bg-green-600 hover:bg-green-700 text-sm rounded transition-colors"
                          >
                            Enable
                          </button>
                        ) : null}
                        <button
                          onClick={() => setDeleteConfirm({ realmId: tenant.realmId, confirmText: '' })}
                          className="px-3 py-1 bg-red-600 hover:bg-red-700 text-sm rounded transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
